import { useEffect, useState } from "react"
import { Box, Stack, TableCell, TableRow, useTheme } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { DateDisplay } from "lib/@components/dateDisplay"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { BlockClicks } from "lib/@components/block-clicks"
import { ContextMenuBar } from "lib/@components/context-menu-bar"
import { If } from "lib/@components/if"
import { LEGISLATION_FOLDER } from "../legislation-library-helpers"
import { LegislationSchedulesPopover } from "./legislation-schedules-popover"
import { navigateLegislationDetail } from "../legislation-library-navigation"

export function LegislationLibraryRow({ item }) {
    const theme = useTheme()
    const { state, openFolder } = useBoundContext()
    const [schedulesAnchor, setSchedulesAnchor] = useState(null)

    const currentNode = state?.lookup?.get(item?.id)
    const [node, setNode] = useState(item)
    useEffect(syncNode, [currentNode])

    const isFolder = node?.type === LEGISLATION_FOLDER
    const hasSchedules = !isFolder && (node?.scheduleCount ?? 0) > 0
    const label = isFolder ? node?.name : node?.title
    const date = node?.lastModified

    const icon = isFolder ? "mdi:folder" : "mdi:scale-balance"
    const iconColor = theme.palette.sfg20.medium

    return (
        <TableRow hover sx={{ cursor: "pointer" }} data-cy="legislation-library-row" onClick={handleClick}>
            <TableCell sx={{ overflow: "hidden" }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ overflow: "hidden", whiteSpace: "nowrap" }}
                >
                    <Iconify icon={icon} sx={{ width: 22, height: 22, color: iconColor, flexShrink: 0 }} />
                    <TruncatedTypography
                        noWrap
                        sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {label}
                    </TruncatedTypography>
                </Stack>
            </TableCell>
            <TableCell>
                <If truthy={!isFolder}>
                    <Box
                        component="span"
                        onClick={hasSchedules ? handleSchedulesOpen : undefined}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            cursor: hasSchedules ? "pointer" : "default",
                            "&:hover": hasSchedules ? { bgcolor: theme.palette.action.hover } : undefined,
                        }}
                    >
                        <Iconify icon="mdi:file-document-outline" sx={{ width: 18, height: 18, flexShrink: 0 }} />
                        <span>{node?.scheduleCount ?? 0}</span>
                        <Iconify
                            icon="mdi:chevron-down"
                            sx={{ width: 16, height: 16, color: theme.palette.text.disabled, flexShrink: 0 }}
                        />
                    </Box>
                </If>
                <BlockClicks>
                    <LegislationSchedulesPopover
                        anchorEl={schedulesAnchor}
                        onClose={handleSchedulesClose}
                        schedules={node?.schedules ?? []}
                        customScheduleCount={node?.customScheduleCount ?? 0}
                    />
                </BlockClicks>
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap", color: theme.palette.text.secondary }}>
                <DateDisplay key={date} date={date} />
            </TableCell>
            <TableCell align="right" sx={{ width: 56 }}>
                <ContextMenuBar type="legislation-library-item" item={node} />
            </TableCell>
        </TableRow>
    )

    function syncNode() {
        if (currentNode) setNode(currentNode)
    }

    function handleClick() {
        if (isFolder) openFolder(item.id)
        else navigateLegislationDetail(item.id)
    }

    function handleSchedulesOpen(event) {
        event.stopPropagation()
        setSchedulesAnchor(event.currentTarget)
    }

    function handleSchedulesClose() {
        setSchedulesAnchor(null)
    }
}
